import React from "react"
import { PageDrawerAction } from "src/components/ui/PageDrawerAction"
import { Icons } from "src/components/ui/Icons"
import { useActions, useAppState, useReaction } from "src/state"
import { 
    MessageFragment,
    Messages_Insert_Input,
    UpdateMessageMutationVariables
} from "src/state/gql/_types"
import { FormDrawer } from "src/components/forms/FormDrawer"
import { Loader } from "@mantine/core"
import { 
    SuperAdminThreadMessageCreateFragment, 
    SuperAdminThreadMessageCreateSchema, 
    SuperAdminThreadMessageUpdateFragment, 
    SuperAdminThreadMessageUpdateSchema 
} from "src/components/superadmin/threads/messages/SuperAdminThreadMessageSchemas"
import { extractCurrentItemData } from "src/utils/forms"
import { FormInline } from "src/components/forms/FormInline"

type FormPayloadType = SuperAdminThreadMessageCreateFragment

let initialState: FormPayloadType = {
    body: '',
};

export const CreateSuperAdminThreadMessageInline: React.FC<{onClose?():void}> = ({ onClose }) => {

    const { createItem, updateItem, deleteItem } = useActions().messages;
    const { loading, errors } = useAppState().messages;
    const { currentItemId: currentThreadId } = useAppState().threads;
    
    let currentItemId = null;
    let currentItemData: FormPayloadType | null = null;

    const handleCreate = (payload: Messages_Insert_Input) => {
        return createItem({ 
            thread_id: currentThreadId,
            idem_key: crypto.randomUUID(),
            ...payload
        })
    }

    return (
        <>
            <FormInline<FormPayloadType, MessageFragment, string>
                formSchema={SuperAdminThreadMessageCreateSchema}
                createAction={handleCreate}
                updateAction={updateItem}
                deleteAction={deleteItem}
                isLoading={loading}
                errors={errors}
                currentItemId={currentItemId}
                initialData={initialState}
                currentItemData={currentItemData}
                onClose={onClose}
                deleteRedirect={true}
                buttonText="Send Reply"
                buttonIcon="iconamoon:send-duotone"
            />

        </>
    )

}

let initialStateUpdate: SuperAdminThreadMessageUpdateFragment = {
    body: '',
};

export const UpdateSuperAdminThreadMessageDrawer: React.FC<{onClose():void,deselect:boolean}> = ({ onClose, deselect = true }) => {

    
    const { updateItem, deleteItem, deselectItem } = useActions().messages;
    const { currentItem, loading, errors } = useAppState().messages;

    if (loading || !currentItem) return <Loader />

    let currentItemId = null;
    let currentItemData: SuperAdminThreadMessageUpdateFragment | null = null;
    if (currentItem) {
        currentItemId = currentItem.id;
        currentItemData = extractCurrentItemData<SuperAdminThreadMessageUpdateFragment>(initialStateUpdate, currentItem);
    }

    const handleClose = () => {
        if(deselect) {
            deselectItem()
        }
        onClose()
    }

    const handleUpdate = (payload: UpdateMessageMutationVariables) => {
        return updateItem({
            id: payload.id,
            _set: {
                ...payload._set
            }
        })
    }

    return (
        <>
            <FormDrawer<SuperAdminThreadMessageUpdateFragment, MessageFragment, string>
                autoOpen={false}
                formSchema={SuperAdminThreadMessageUpdateSchema}
                updateAction={handleUpdate}
                deleteAction={deleteItem}
                isLoading={loading}
                errors={errors}
                currentItemId={currentItemId}
                initialData={initialStateUpdate}
                currentItemData={currentItemData}
                onClose={handleClose}
                deleteRedirect={true}
            />

        </>
    )

}

export const UpdateSuperAdminThreadMessageAction = () => {

    const reaction = useReaction()
    const { deselectItem } = useActions().messages;
    const [opened, setOpened] = React.useState(false);

    React.useEffect(() => reaction(
        (state) => state.messages.currentItemId,
        (selectedID) => {
            setOpened(!!selectedID)
        }, { immediate: false }
    ))

    const handleClose = () => {
        deselectItem()
    }

    return (
        <PageDrawerAction<MessageFragment>
            title='Edit Message'
            action={UpdateSuperAdminThreadMessageDrawer}
            icon={Icons.edit}
            startOpened={opened}
            onClose={handleClose}
            hidden
        />
    )
}

export const EditSuperAdminThreadMessageAction = () => {
    return (
        <PageDrawerAction<MessageFragment>
            title='Edit Message'
            action={UpdateSuperAdminThreadMessageDrawer}
            icon={Icons.edit}
            props={{
                deselect: false
            }}
        />
    )
}